import { sql } from 'drizzle-orm';
import { index, integer, primaryKey, sqliteTable, text, uniqueIndex } from 'drizzle-orm/sqlite-core';
import { customAlphabet } from 'nanoid';
import type { AdapterAccountType } from 'next-auth/adapters';

export const nanoid = customAlphabet('abcdefghijklmnopqrstuvwxyz', 8);

export enum Role {
  Admin = 'admin',
  Partner = 'partner',
  Affiliate = 'affiliate',
  Provider = 'provider',
  Patient = 'patient',
}

export enum Notification {
  Email = 'email',
  Sms = 'sms',
  Push = 'push',
}

export const users = sqliteTable('user', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  name: text('name'),
  email: text('email').notNull(),
  emailVerified: integer('emailVerified', { mode: 'timestamp_ms' }),
  image: text('image'),
  company: text('company'),
  phone: text('phone'),
  // Provider's handwritten signature as a small data-URI PNG (downscaled client-side).
  // Embedded rather than hosted so html2canvas PDF downloads never hit CORS tainting.
  signature: text('signature'),
  pushTokens: text('pushTokens', { mode: 'json' }).$type<Array<string>>(),
  notifications: text('notifications', { mode: 'json' }).$type<Array<Notification>>().default([Notification.Email]),
  migrated: integer('migrated', { mode: 'boolean' }).notNull().default(false),
  roles: text('roles', { mode: 'json' }).$type<Array<Role>>().notNull().default([Role.Patient]),
  secretTimestamp: integer('secretTimestamp', { mode: 'timestamp_ms' }),
  lastSignIn: integer('lastSignIn', { mode: 'timestamp_ms' }),
  promotionCode: text('promotionCode'),
  tracking: text('tracking'),
  updated: text('updated').$onUpdate(() => sql`(current_timestamp)`),
  created: text('created').default(sql`(current_timestamp)`),
});

export const accounts = sqliteTable(
  'account',
  {
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    type: text('type').$type<AdapterAccountType>().notNull(),
    provider: text('provider').notNull(),
    providerAccountId: text('providerAccountId').notNull(),
    refresh_token: text('refresh_token'),
    access_token: text('access_token'),
    expires_at: integer('expires_at'),
    token_type: text('token_type'),
    scope: text('scope'),
    id_token: text('id_token'),
    session_state: text('session_state'),
  },
  (account) => ({
    compoundKey: primaryKey({
      columns: [account.provider, account.providerAccountId],
    }),
  })
);

export const sessions = sqliteTable('session', {
  sessionToken: text('sessionToken').primaryKey(),
  userId: text('userId')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  expires: integer('expires', { mode: 'timestamp_ms' }).notNull(),
});

export const verificationTokens = sqliteTable(
  'verificationToken',
  {
    identifier: text('identifier').notNull(),
    token: text('token').notNull(),
    expires: integer('expires', { mode: 'timestamp_ms' }).notNull(),
  },
  (verificationToken) => ({
    compositePk: primaryKey({
      columns: [verificationToken.identifier, verificationToken.token],
    }),
  })
);

export const authenticators = sqliteTable(
  'authenticator',
  {
    credentialID: text('credentialID').notNull().unique(),
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    providerAccountId: text('providerAccountId').notNull(),
    credentialPublicKey: text('credentialPublicKey').notNull(),
    counter: integer('counter').notNull(),
    credentialDeviceType: text('credentialDeviceType').notNull(),
    credentialBackedUp: integer('credentialBackedUp', {
      mode: 'boolean',
    }).notNull(),
    transports: text('transports'),
  },
  (authenticator) => ({
    compositePK: primaryKey({
      columns: [authenticator.userId, authenticator.credentialID],
    }),
  })
);

export enum PartnerType {
  Affiliate = 'affiliate',
  Provider = 'provider',
}
export enum PartnerApplicationStatus {
  Pending = 'pending',
  EditsRequested = 'edits_requested',
  Accepted = 'accepted',
  Rejected = 'rejected',
  Inactive = 'inactive',
}
export enum PayoutType {
  Fixed = 'fixed',
  Percentage = 'percentage',
}

/**
 * We need a two-level affiliate system.
 * Affiliates can be assigned a parentAffiliateId
 * Payments go to the original affiliate, and then get payed out to the parentAffiliateId
 * as well.
 */
export const partnerApplications = sqliteTable(
  'partnerApplication',
  {
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    type: text('type', { enum: [PartnerType.Affiliate, PartnerType.Provider] }).notNull(),
    status: text('status', {
      enum: [
        PartnerApplicationStatus.Pending,
        PartnerApplicationStatus.EditsRequested,
        PartnerApplicationStatus.Accepted,
        PartnerApplicationStatus.Rejected,
        PartnerApplicationStatus.Inactive,
      ],
    }).notNull(),
    markdown: text('markdown'),

    payoutType: text('payoutType', { enum: [PayoutType.Fixed, PayoutType.Percentage] }).notNull(),
    payoutPercentage: integer('payoutPercentage').notNull().default(15),
    payoutCents: integer('payoutCents').notNull().default(2000),

    parentAffiliateId: text('parentAffiliateId').references(() => users.id),
    parentPayoutType: text('parentPayoutType', { enum: [PayoutType.Fixed, PayoutType.Percentage] }),
    parentPayoutPercentage: integer('parentPayoutPercentage'),
    parentPayoutCents: integer('parentPayoutCents'),

    /**
     * What this affiliate's /join link grants: the recruit's own rate, and this affiliate's
     * override on the recruit's sales. Read off the referrer's row at the recruit's signup and
     * copied onto the recruit as payoutPercentage / parentPayoutPercentage, so re-rating a
     * referrer never moves a rate someone already signed. Null falls back to the constants in
     * lib/affiliate-referral-rates.
     */
    recruitPayoutPercentage: integer('recruitPayoutPercentage'),
    recruitParentPayoutPercentage: integer('recruitParentPayoutPercentage'),

    signedAt: text('signedAt'),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    compositePk: primaryKey({
      columns: [table.userId, table.type],
    }),
  })
);

export const promotionCodes = sqliteTable('promotionCode', {
  id: text('id').primaryKey(),
  code: text('code'),
  userId: text('userId')
    .notNull()
    .unique()
    .references(() => users.id, { onDelete: 'cascade' }),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export enum BusinessType {
  Individual = 'individual',
  Company = 'company',
  GovernmentEntity = 'government_entity',
  NonProfit = 'non_profit',
  Unknown = 'unknown',
}

export enum StripeAccountType {
  Custom = 'custom',
  Express = 'express',
  None = 'none',
  Standard = 'standard',
}

export const stripeAccounts = sqliteTable('stripeAccount', {
  id: text('id').primaryKey(),
  userId: text('userId')
    .notNull()
    .unique()
    .references(() => users.id, { onDelete: 'cascade' }),
  email: text('email').notNull(),
  type: text('type', {
    enum: [StripeAccountType.Custom, StripeAccountType.Express, StripeAccountType.None, StripeAccountType.Standard],
  }).notNull(),
  businessType: text('businessType', {
    enum: [BusinessType.Individual, BusinessType.Company, BusinessType.GovernmentEntity, BusinessType.NonProfit],
  }).notNull(),
  json: text('json', { mode: 'json' }).notNull(),
  updated: text('updated')
    .notNull()
    .$onUpdate(() => sql`(current_timestamp)`),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export enum ContactTime {
  Morning = 'Morning',
  Noon = 'Noon',
  Evening = 'Evening',
}

export enum CheckoutState {
  Idle = 'idle',
  Pending = 'pending',
  Completed = 'completed',
}

export enum VerificationService {
  OurPetPolicy = 'Our Pet Policy',
  PetScreening = 'PetScreening',
  Other = 'Other',
}

export const intakeForms = sqliteTable(
  'intakeForm',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => nanoid()),
    userId: text('userId')
      .unique()
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    assignedProviderId: text('assignedProviderId').references(() => users.id),
    claimedProviderId: text('claimedProviderId').references(() => users.id),

    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
    submitted: text('submitted'),
    assignedAt: text('assignedAt'),
    claimedAt: text('claimedAt'),
    deniedAt: text('deniedAt'),
    issuedAt: text('issuedAt'),
    canceledAt: text('canceledAt'),
    // Set when every eligible provider has passed on (or unclaimed) this form for the current
    // checkout cycle, so the round robin cannot place it. Cleared on assignment/claim.
    manualAssignmentRequiredAt: text('manualAssignmentRequiredAt'),
    lastFollowUp: text('lastFollowUp'),
    checkoutState: text('checkoutState').$type<CheckoutState>().notNull().default(CheckoutState.Idle),
    checkoutStateUpdatedAt: text('checkoutStateUpdatedAt'),
    checkoutPaymentIntent: text('checkoutPaymentIntent'),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),

    answers: text('answers', { mode: 'json' }).notNull(),
    firstName: text('firstName').notNull(),
    lastName: text('lastName').notNull(),
    email: text('email').notNull(),
    phoneNumber: text('phoneNumber').notNull(),
    preferredContactTime: text('preferredContactTime', {
      enum: [ContactTime.Morning, ContactTime.Noon, ContactTime.Evening],
    }).notNull(),
    state: text('state').notNull(),
    city: text('city').notNull(),

    housingProvider: text('housingProvider'),
    verificationService: text('verificationService', {
      enum: [VerificationService.OurPetPolicy, VerificationService.PetScreening, VerificationService.Other],
    }),

    depression: integer('depression').notNull(),
    ptsd: integer('ptsd').notNull(),
    anxiety: integer('anxiety').notNull(),
    bipolar: integer('bipolar').notNull(),
    psychosis: integer('psychosis').notNull(),
    ocd: integer('ocd').notNull(),
    dissociation: integer('dissociation').notNull(),
    points: integer('points').notNull(),
  },
  (table) => ({
    assignedProviderIdIdx: index('intakeForm_assignedProviderId_idx').on(table.assignedProviderId),
    claimedProviderIdIdx: index('intakeForm_claimedProviderId_idx').on(table.claimedProviderId),
  })
);

export enum PetState {
  Pending = 'pending',
  Approved = 'approved',
  Denied = 'denied',
}

export const pets = sqliteTable('pet', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  userId: text('userId')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  workflowId: text('workflowId').references(() => workflows.id),

  name: text('name'),
  species: text('species'),
  breed: text('breed'),
  pounds: integer('pounds'),

  state: text('state').$type<PetState>().notNull().default(PetState.Pending),
  paidAt: text('paidAt'),
  evaluatedAt: text('evaluatedAt'),
  evaluatedBy: text('evaluatedBy').references(() => users.id),

  updated: text('updated')
    .notNull()
    .$onUpdate(() => sql`(current_timestamp)`),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export enum PayoutState {
  Pending = 'pending',
  Paid = 'paid',
  Failed = 'failed',
  Canceled = 'canceled',
}

export const stripeCharges = sqliteTable(
  'stripeCharge',
  {
    id: text('id').primaryKey(),
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    paymentIntent: text('paymentIntent').notNull(),
    amount: integer('amount').notNull(),
    amountCaptured: integer('amountCaptured').notNull(),
    amountRefunded: integer('amountRefunded').notNull(),
    lineItems: text('lineItems', { mode: 'json' }).notNull(),
    code: text('code'),
    json: text('json', { mode: 'json' }).notNull(),
    payoutState: text('payoutState').$type<PayoutState>().notNull().default(PayoutState.Pending),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    userIdCreatedIdx: index('stripeCharge_userId_created_idx').on(table.userId, table.created),
  })
);

/**
 * Admins manage this table.
 * Admins can "pause" a provider in a state by setting active to false.
 * Once a record is created, it is never deleted, only set to inactive.
 */
export const providerStates = sqliteTable(
  'providerState',
  {
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    state: text('state').notNull(),
    active: integer('active', { mode: 'boolean' }).notNull().default(true),
    lastAssigned: text('lastAssigned'),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    compositePk: primaryKey({
      columns: [table.userId, table.state],
    }),
  })
);

/**
 * Working hours are stored in UTC time.
 * timezoneOffset is set to whatever the local timezone is on save.
 * Users can "pause" their working hours by setting active to false.
 */
export const workingHours = sqliteTable(
  'workingHours',
  {
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    day: text('day').notNull(),
    timezoneOffset: integer('timezoneOffset').notNull().default(0),
    start: text('start'),
    end: text('end'),

    active: integer('active', { mode: 'boolean' }).notNull().default(true),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    compositePk: primaryKey({
      columns: [table.userId, table.day],
    }),
  })
);

export enum WorkflowState {
  Unscheduled = 'unscheduled',
  Scheduled = 'scheduled',
  Called = 'called',
  SecondCallPending = 'secondCallPending',
  CallFailed = 'callFailed',
  Unresponsive = 'unresponsive',
  Approved = 'approved',
  Denied = 'denied',
  ProviderPaid = 'providerPaid',
  Canceled = 'Canceled',
}

export const WORKFLOW_STATE_DESCRIPTIONS = {
  [WorkflowState.Unscheduled]: 'Unscheduled',
  [WorkflowState.Scheduled]: 'Scheduled',
  [WorkflowState.Called]: 'First call',
  [WorkflowState.SecondCallPending]: 'Second call pending',
  [WorkflowState.CallFailed]: 'Call failed',
  [WorkflowState.Unresponsive]: 'Unresponsive',
  [WorkflowState.Approved]: 'Approved',
  [WorkflowState.Denied]: 'Denied',
  [WorkflowState.ProviderPaid]: 'Provider paid',
  [WorkflowState.Canceled]: 'Canceled',
};

export const workflows = sqliteTable(
  'workflow',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => nanoid()),
    intakeFormId: text('intakeFormId').references(() => intakeForms.id),
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    providerId: text('providerId').references(() => users.id),
    state: text('state').notNull().$type<WorkflowState>().default(WorkflowState.Unscheduled),
    notes: text('notes'),
    lastNotification: text('lastNotification'),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    userIdIdx: index('workflow_userId_idx').on(table.userId),
  })
);

export const payouts = sqliteTable(
  'payout',
  {
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    stripeChargeId: text('stripeChargeId').notNull(),
    stripePaymentId: text('stripePaymentId'),
    stripeTransferId: text('stripeTransferId'),
    amountCents: integer('amount').notNull(),
    delayDays: integer('delayDays').notNull().default(0),
    paidAt: text('paidAt'),
    updated: text('updated')
      .notNull()
      .$onUpdate(() => sql`(current_timestamp)`),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    compositePk: primaryKey({
      columns: [table.userId, table.stripeChargeId],
    }),
  })
);

export const licenses = sqliteTable(
  'license',
  {
    userId: text('userId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    state: text('state').notNull(),
    licenseType: text('licenseType').notNull(),
    licenseNumber: text('licenseNumber').notNull(),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    compositePk: primaryKey({
      columns: [table.userId, table.state],
    }),
  })
);

export const adhocPromotionCodes = sqliteTable('adhocPromotionCode', {
  code: text('code')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  cents: integer('cents'),
  percentage: integer('percentage'),
  active: integer('active', { mode: 'boolean' }).notNull().default(true),
  expiresAt: text('expiresAt'),

  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const letters = sqliteTable('letter', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  userId: text('userId')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  providerId: text('providerId').references(() => users.id),
  workflowId: text('workflowId').references(() => workflows.id),
  state: text('state').notNull(),
  text: text('text'),
  template: text('template').notNull().default(''),
  header: text('header'),
  footer: text('footer'),
  url: text('url'),
  sent: text('sent'),
  updated: text('updated')
    .notNull()
    .$onUpdate(() => sql`(current_timestamp)`),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const templates = sqliteTable('template', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  userId: text('userId')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  title: text('title').notNull(),
  text: text('text').notNull(),
  header: text('header'),
  footer: text('footer'),
  updated: text('updated')
    .notNull()
    .$onUpdate(() => sql`(current_timestamp)`),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const messages = sqliteTable('message', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  userId: text('userId')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  to: text('to'),
  from: text('from'),
  body: text('body'),
  read: integer('read', { mode: 'boolean' }).notNull().default(false),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export enum ProviderDecisionAction {
  Passed = 'passed',
  Unclaimed = 'unclaimed',
}

/**
 * Records a provider explicitly declining a patient: "passed" on an offered assignment,
 * or "unclaimed" a patient they previously claimed (or were assigned by an admin).
 *
 * checkoutCycle scopes the decision to a single paid checkout (the intake form's
 * checkoutPaymentIntent, or 'legacy' for forms that predate checkout tracking).
 * The round robin never re-offers a form to a provider with a decision for the
 * current cycle, but a future renewal (new payment intent) starts with a clean slate.
 */
export const providerDecisions = sqliteTable(
  'providerDecision',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => nanoid()),
    intakeFormId: text('intakeFormId')
      .notNull()
      .references(() => intakeForms.id, { onDelete: 'cascade' }),
    providerId: text('providerId')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    action: text('action').$type<ProviderDecisionAction>().notNull(),
    checkoutCycle: text('checkoutCycle').notNull(),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    intakeFormCycleIdx: index('providerDecision_intakeFormId_checkoutCycle_idx').on(
      table.intakeFormId,
      table.checkoutCycle
    ),
    providerIdIdx: index('providerDecision_providerId_idx').on(table.providerId),
  })
);

export enum ProviderNotificationReason {
  Assignment = 'assignment',
  RepeatCustomer = 'repeatCustomer',
  NoProviderAvailable = 'noProviderAvailable',
  NoProviderOnDuty = 'noProviderOnDuty',
}

/**
 * Audit log of provider-facing queue notifications (and admin exhaustion alerts).
 * Doubles as the idempotency guard: before sending, callers check for an existing
 * row with the same (intakeFormId, providerId, reason, checkoutCycle).
 * A non-null error means the send failed — these rows do NOT count as delivered.
 */
export const providerQueueNotifications = sqliteTable(
  'providerQueueNotification',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => nanoid()),
    intakeFormId: text('intakeFormId')
      .notNull()
      .references(() => intakeForms.id, { onDelete: 'cascade' }),
    // Null for admin-facing alerts (e.g. noProviderAvailable).
    providerId: text('providerId').references(() => users.id, { onDelete: 'cascade' }),
    reason: text('reason').$type<ProviderNotificationReason>().notNull(),
    checkoutCycle: text('checkoutCycle').notNull(),
    channels: text('channels', { mode: 'json' }).$type<Array<Notification>>(),
    error: text('error'),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    intakeFormReasonCycleIdx: index('providerQueueNotification_intakeFormId_reason_checkoutCycle_idx').on(
      table.intakeFormId,
      table.reason,
      table.checkoutCycle
    ),
  })
);

export const smsLogs = sqliteTable('smsLog', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  sid: text('sid'),
  to: text('to'),
  from: text('from'),
  body: text('body'),
  status: text('status'),
  direction: text('direction'),
  errorCode: text('errorCode'),
  errorMessage: text('errorMessage'),
  json: text('json', { mode: 'json' }),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const messagingDecisions = sqliteTable('messagingDecision', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  customerId: text('customerId').notNull(),
  messageType: text('messageType').notNull(),
  decision: text('decision').notNull(), // 'sent' | 'blocked'
  reason: text('reason').notNull(),
  channel: text('channel').notNull(), // 'sms' | 'email'
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const providerNotifications = sqliteTable('providerNotification', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  orderId: text('orderId').notNull(),
  providerId: text('providerId').notNull(),
  reason: text('reason').notNull(),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export const partialRefunds = sqliteTable('partialRefund', {
  id: text('id')
    .primaryKey()
    .$defaultFn(() => nanoid()),
  orderId: text('orderId').notNull(),
  stripeRefundId: text('stripeRefundId').notNull(),
  amountCents: integer('amountCents').notNull(),
  reason: text('reason').notNull(),
  notes: text('notes'),
  source: text('source').notNull().default('intercom_fin'),
  created: text('created')
    .notNull()
    .default(sql`(current_timestamp)`),
});

export enum CancellationAction {
  Canceled = 'canceled',
  Restored = 'restored',
}

export enum CancellationReason {
  Unresponsive = 'unresponsive',
  WrongProduct = 'wrongProduct',
  DuplicateAccount = 'duplicateAccount',
  PatientRequested = 'patientRequested',
  NeverPaid = 'neverPaid',
  Other = 'other',
}

/**
 * What happened to the customer's money. `revenueKept` is a deliberate, recorded choice —
 * the whole point of this table is that "we kept the money" can never again be indistinguishable
 * from "nobody remembered to look".
 */
export enum CancellationResolution {
  NoMoneyCaptured = 'noMoneyCaptured',
  Refunded = 'refunded',
  RevenueKept = 'revenueKept',
  NotApplicable = 'notApplicable',
  // The status dropdown can't ask for a refund decision (providers use it too and may not have
  // refund authority), so it records this and the paid-but-unresolved report chases it down.
  Unresolved = 'unresolved',
}

export const CANCELLATION_REASON_DESCRIPTIONS = {
  [CancellationReason.Unresponsive]: 'Unresponsive to contact attempts',
  [CancellationReason.WrongProduct]: 'Wanted a different product/service',
  [CancellationReason.DuplicateAccount]: 'Duplicate account',
  [CancellationReason.PatientRequested]: 'Patient asked to cancel',
  [CancellationReason.NeverPaid]: 'Never paid',
  [CancellationReason.Other]: 'Other (explain in notes)',
};

/**
 * Append-only audit log of cancellations and restores. Rows are never deleted or updated, so
 * a cancel → restore → cancel history stays legible. `intakeForm.canceledAt` remains the
 * single source of truth for *current* state; this table records how and why it got there.
 */
export const cancellations = sqliteTable(
  'cancellation',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => nanoid()),
    intakeFormId: text('intakeFormId')
      .notNull()
      .references(() => intakeForms.id, { onDelete: 'cascade' }),
    // Null when a system path (e.g. a backfill) recorded the event rather than a person.
    actorId: text('actorId').references(() => users.id, { onDelete: 'set null' }),
    action: text('action').$type<CancellationAction>().notNull(),
    reason: text('reason').$type<CancellationReason>(),
    resolution: text('resolution').$type<CancellationResolution>(),
    notes: text('notes'),
    // Money position at the moment of the decision, so later refunds/charges can't rewrite history.
    netCapturedCents: integer('netCapturedCents').notNull().default(0),
    stripeRefundIds: text('stripeRefundIds', { mode: 'json' }).$type<string[]>(),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    intakeFormIdx: index('cancellation_intakeFormId_idx').on(table.intakeFormId),
  })
);

export enum EmailEventType {
  Bounce = 'bounce',
  Dropped = 'dropped',
  Delivered = 'delivered',
}

/**
 * Deliverability events from the SendGrid event webhook.
 *
 * Until this existed nothing in the system learned that mail had failed, so a mistyped
 * address only surfaced weeks later as a support ticket. The email is stored as sent
 * rather than joined to a user, because a bounce is a fact about an address and the
 * address may belong to nobody once an admin corrects it.
 */
export const emailEvents = sqliteTable(
  'emailEvent',
  {
    id: text('id')
      .primaryKey()
      .$defaultFn(() => nanoid()),
    email: text('email').notNull(),
    // Null when the address matches no account, e.g. mail to a provider or a stale record.
    userId: text('userId').references(() => users.id, { onDelete: 'set null' }),
    event: text('event').$type<EmailEventType>().notNull(),
    // SendGrid's own words for why, kept verbatim for support to read.
    reason: text('reason'),
    // 'bounce' is a hard rejection, 'blocked' is a soft one worth retrying.
    bounceType: text('bounceType'),
    sgMessageId: text('sgMessageId'),
    // SendGrid retries and can deliver the same event twice; this is the dedupe key.
    sgEventId: text('sgEventId').notNull(),
    occurredAt: integer('occurredAt', { mode: 'timestamp_ms' }).notNull(),
    created: text('created')
      .notNull()
      .default(sql`(current_timestamp)`),
  },
  (table) => ({
    emailIdx: index('emailEvent_email_idx').on(table.email),
    userIdIdx: index('emailEvent_userId_idx').on(table.userId),
    sgEventIdIdx: uniqueIndex('emailEvent_sgEventId_idx').on(table.sgEventId),
  })
);
